/**
 * Broker RBAC — Roles & Permissions
 *
 * Mirrors the backend EnumSet definitions.
 * psRole comes from the JWT token (field: psRole) and from /api/account (partnerStaffRole).
 */

// ── Permissions ──
export enum Permission {
  COMPETITION_LIST_ALL = 'COMPETITION_LIST_ALL',
  COMPETITION_LIST_MY = 'COMPETITION_LIST_MY',
  COMPETITION_CREATE = 'COMPETITION_CREATE',

  IB_LIST_ALL = 'IB_LIST_ALL',
  IB_LIST_MY = 'IB_LIST_MY',
  IB_CREATE = 'IB_CREATE',

  STAFF_LIST_ALL = 'STAFF_LIST_ALL',
  STAFF_CREATE = 'STAFF_CREATE',

  PLATFORM_GROUP_LIST_ALL = 'PLATFORM_GROUP_LIST_ALL',
}

// ── Roles ──
export enum StaffRole {
  ADMIN = 'ADMIN', // Legacy/super-admin — full access, not assignable to new staff
  MANAGER = 'MANAGER',
  BUSINESS_DEVELOPER = 'BUSINESS_DEVELOPER',
  OPERATIONS = 'OPERATIONS',
  READ_ONLY = 'READ_ONLY',
}

// ── Role → Permissions mapping ──
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  // ADMIN gets all permissions (existing users in DB, not offered in invite form)
  [StaffRole.ADMIN]: Object.values(Permission),

  [StaffRole.MANAGER]: [
    Permission.COMPETITION_LIST_ALL,
    Permission.COMPETITION_CREATE,
    Permission.IB_LIST_ALL,
    Permission.IB_CREATE,
    Permission.STAFF_LIST_ALL,
    Permission.STAFF_CREATE,
    Permission.PLATFORM_GROUP_LIST_ALL,
  ],

  [StaffRole.BUSINESS_DEVELOPER]: [
    Permission.COMPETITION_LIST_MY,
    Permission.COMPETITION_CREATE,
    Permission.IB_LIST_MY,
    Permission.IB_CREATE,
  ],

  // Mirrors backend PartnerStaffRole: OPERATIONS shares MANAGER's full set and
  // takes part in the partner-wide access paths (SFB-1234 follow-up on the
  // backend: "grant the OPERATIONS partner staff role the same access as MANAGER").
  [StaffRole.OPERATIONS]: [
    Permission.COMPETITION_LIST_ALL,
    Permission.COMPETITION_CREATE,
    Permission.IB_LIST_ALL,
    Permission.IB_CREATE,
    Permission.STAFF_LIST_ALL,
    Permission.STAFF_CREATE,
    Permission.PLATFORM_GROUP_LIST_ALL,
  ],

  [StaffRole.READ_ONLY]: [
    Permission.PLATFORM_GROUP_LIST_ALL,
  ],
}

/**
 * Get permissions for a given staff role.
 * Returns empty array for unknown roles.
 */
export function getPermissions(role: string | undefined | null): Permission[] {
  if (!role) return []
  return ROLE_PERMISSIONS[role] || []
}

/**
 * Check if a role has a specific permission.
 */
export function hasPermission(role: string | undefined | null, permission: Permission): boolean {
  return getPermissions(role).includes(permission)
}

/**
 * Check if a role has ANY of the given permissions.
 */
export function hasAnyPermission(role: string | undefined | null, permissions: Permission[]): boolean {
  const rolePerms = getPermissions(role)
  return permissions.some(p => rolePerms.includes(p))
}

/**
 * Role display labels (for UI).
 *
 * SFB-1270: the legacy ADMIN staff role is presented as "Manager". The backend
 * PartnerStaffRole enum has no ADMIN value any more and MANAGER already holds
 * the full permission set, so exposing both names only read as two manager
 * roles. Permissions are untouched — only the wording is consolidated.
 */
export const ROLE_LABELS: Record<string, string> = {
  [StaffRole.ADMIN]: 'Manager',
  [StaffRole.MANAGER]: 'Manager',
  [StaffRole.BUSINESS_DEVELOPER]: 'Business Developer',
  [StaffRole.OPERATIONS]: 'Operations',
  [StaffRole.READ_ONLY]: 'Read Only',
}

/** CSS modifier per role for the role chips (ADMIN shares Manager's look). */
export const ROLE_CSS_CLASSES: Record<string, string> = {
  [StaffRole.ADMIN]: 'manager',
  [StaffRole.MANAGER]: 'manager',
  [StaffRole.BUSINESS_DEVELOPER]: 'business-developer',
  [StaffRole.OPERATIONS]: 'operations',
  [StaffRole.READ_ONLY]: 'operations',
}

export function staffRoleLabel(role: string | undefined | null): string {
  return (role && ROLE_LABELS[role]) || role || ''
}

export function staffRoleCssClass(role: string | undefined | null): string {
  return (role && ROLE_CSS_CLASSES[role]) || 'operations'
}

/**
 * Whether two role values should be treated as the same role in the UI
 * (legacy ADMIN counts as MANAGER for filters and selections).
 */
export function isSameStaffRole(a: string | undefined | null, b: string | undefined | null): boolean {
  const norm = (r: string | undefined | null) => (r === StaffRole.ADMIN ? StaffRole.MANAGER : r)
  return !!a && !!b && norm(a) === norm(b)
}

/**
 * Roles available for invitation
 */
export const INVITABLE_ROLES = [
  { text: 'Manager', value: StaffRole.MANAGER },
  { text: 'Business Developer', value: StaffRole.BUSINESS_DEVELOPER },
  { text: 'Operations', value: StaffRole.OPERATIONS },
]
